/**
 * Per-placement material copies — the mechanism that keeps a project's
 * material edits out of the asset library and out of every other project.
 *
 * ## Why this exists
 *
 * A material row is **customer-scoped**: `pk = "{customerId}:material"`,
 * `sk = "$material:{materialId}"` (`HttpStorageModel.materialDtoToDynamoItem`,
 * and identically `DynamoClient.cs:1346-1352` — `updateMaterial` even takes a
 * `projectId` it never reads). There is no project or scene component in the
 * key, so **two snapshots naming one material id are literally reading and
 * writing the same database row**, whichever projects they live in.
 *
 * Unity does not solve that at the storage layer. It solves it by never
 * letting two placements name the same id: `CreateMaterialDataAsync`
 * (`ProjectModel_UploadAsset.cs:597-684`) mints a fresh id per distinct
 * material on **every** spawn — including the place-from-library branch, where
 * `newAsset == false`. The ternary at `:207`
 * (`newAsset ? partsData : null`) decides only whether the *part* rows are
 * rewritten; the snapshot always gets the fresh ids (`:673`) and the part row
 * only when the asset is brand new (`:675-677`). So a library part row keeps
 * the library's ids forever, and each placement owns a private generation.
 *
 * That is the whole isolation mechanism. Unity has **no copy-on-write at edit
 * time** — `ProjectModel_Entities.UpdateMaterialAsync` (`:798`) resolves
 * `state.GetID(material)` to the existing id and overwrites that row in place.
 * It can only ever reach a placement-local row because the placement minted
 * one.
 *
 * Save to Library mints a *third* generation in the other direction
 * (`ProjectDataModel.cs:2103-2126` — fresh ids onto `part.MaterialIDs`, source
 * snapshots untouched), which is why a library update never propagates back
 * into projects that already placed the asset.
 *
 * ## What this helper does
 *
 * Unity walks the live `Material[]` on the instantiated GameObject; the web
 * has no instance at placement time, so it maps over the distinct material
 * **ids** in the part catalog instead and clones the DTO rows. Same outcome,
 * different derivation.
 *
 * Deliberately NOT shared with `ProjectModelSaveToLibrary.copyMaterials`: that
 * one also duplicates every non-shared TEXTURE, because a library entry must
 * own its binaries. A placement copy references the same texture ids, matching
 * Unity — `materialData.FromMaterial(material, textureIDToExistingTexture)`
 * (`:629`) is seeded with the existing textures, so unchanged textures keep
 * their ids and no new binary is written.
 */
import type { MaterialDto } from "@models/data/materialDto";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { randomUuid } from "@models/utils/uuid";

export interface MaterialCloneResult {
  /**
   * source material id → freshly minted copy id.
   *
   * Contains an entry **only** for a source id whose row was readable. A
   * caller must therefore remap with `idMap.get(id) ?? id`, so an orphaned
   * reference keeps its original id rather than pointing at a row that was
   * never written (which the renderer answers with its last-resort
   * `colorFromString` pastel over the whole model).
   */
  readonly idMap: ReadonlyMap<string, string>;
  /** The written rows, so the caller can seed its material cache. */
  readonly copies: ReadonlyArray<MaterialDto>;
}

const EMPTY: MaterialCloneResult = { idMap: new Map(), copies: [] };

/**
 * Clone every resolvable material row named by `sourceIds` under fresh ids and
 * persist the copies.
 *
 * One copy per **distinct** source id, so two parts sharing a library material
 * still share it inside this placement — matching Unity's `materialToData`
 * dictionary, which is keyed by the runtime `Material` instance.
 *
 * Failure policy, and the two halves are deliberately different:
 *
 *  - A source id with **no row** is a data fact we have to tolerate (an
 *    orphaned reference; Unity's own Save-to-Library path stores `null` for
 *    it). It is dropped from the map and the caller keeps the original id.
 *  - A failed **write** is an infrastructure failure and THROWS. Swallowing it
 *    would silently hand back the library's ids and re-create the exact
 *    cross-project leak this function exists to prevent — invisibly, and in a
 *    state a later edit then commits into the library. Callers run this before
 *    mutating any local state, so a throw is clean.
 */
export async function cloneMaterialRows(
  storage: IStorageModel,
  customerId: string,
  sourceIds: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<MaterialCloneResult> {
  const distinct = [...new Set(sourceIds.filter((id): id is string => Boolean(id)))];
  if (distinct.length === 0) return EMPTY;

  const rows = await storage.getMaterialBatch(customerId, distinct, signal);
  const rowById = new Map<string, MaterialDto>();
  for (const row of rows) {
    if (row?.ID) rowById.set(row.ID, row);
  }

  const idMap = new Map<string, string>();
  const copies: MaterialDto[] = [];
  for (const sourceId of distinct) {
    const row = rowById.get(sourceId);
    if (!row) {
      console.warn(
        `[materialCopy] material row ${sourceId} not found — the placement keeps the shared id`,
      );
      continue;
    }
    const copyId = randomUuid();
    idMap.set(sourceId, copyId);
    copies.push({ ...row, ID: copyId });
  }

  if (copies.length === 0) return EMPTY;
  await storage.createMaterials(customerId, copies, signal);
  return { idMap, copies };
}

/**
 * Remap one snapshot's material list through a clone map, preserving **order
 * and length exactly**.
 *
 * Length is load-bearing, not incidental: Unity pads a snapshot from its part
 * row when `entitySnapshot.MaterialIDs.Count < partData.MaterialIDs.Count`
 * (`ProjectDataModel.cs:1031-1046`, the C3D-8297 fallback), and that fallback's
 * own comment warns it re-shares materials across instances — precisely the bug
 * this module prevents. A merged part also carries the combined materials of
 * its subtree in one ordered list, so dropping or reordering an entry
 * mis-assigns renderers.
 */
export function remapMaterialIds(
  materialIds: ReadonlyArray<string>,
  idMap: ReadonlyMap<string, string>,
): string[] {
  return materialIds.map((id) => idMap.get(id) ?? id);
}
